import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';

import { fsErrorMessages } from '@repo/lib/messages/fsErrorMessages';
import { useMemo } from 'react';

import { useGetDirectory } from '#directory/useGetDirectory';
import { useErrorMessage } from '#error-message/useErrorMessage';

import { useGetShared } from './useGetShared';

interface GetDirectoryWithSharedProps {
  directoryId?: string;
  useSharedEndpoint?: boolean;
  enabled?: boolean;
}

export function useGetDirectoryWithShared(getDirectoryWithSharedProps: GetDirectoryWithSharedProps) {
  const { directoryId, useSharedEndpoint, enabled = true } = getDirectoryWithSharedProps;
  const sharedEnabled = enabled && !!useSharedEndpoint;
  const directoryEnabled = enabled && !useSharedEndpoint;

  const sharedQuery = useGetShared(sharedEnabled);
  const directoryQuery = useGetDirectory(directoryId, directoryEnabled);

  const activeQuery = useSharedEndpoint ? sharedQuery : directoryQuery;

  const fsItems = useMemo<{
    dirs: DirectoryPublic[];
    files: FilePublicWithPermissions[];
  }>(
    () =>
      useSharedEndpoint
        ? {
            dirs: sharedQuery.data?.dirs ?? [],
            files: sharedQuery.data?.files ?? [],
          }
        : {
            dirs: directoryQuery.data?.childDirs ?? [],
            files: directoryQuery.data?.files ?? [],
          },
    [useSharedEndpoint, sharedQuery.data, directoryQuery.data]
  );

  const fsMetadata = useMemo(
    () => ({
      directories: fsItems.dirs.length,
      files: fsItems.files.length,
      total: fsItems.dirs.length + fsItems.files.length,
      hasDirectories: fsItems.dirs.length > 0,
      hasFiles: fsItems.files.length > 0,
      isEmpty: fsItems.dirs.length + fsItems.files.length === 0,
    }),
    [fsItems]
  );

  const queryState = {
    isLoading: activeQuery.isLoading,
    isPending: activeQuery.isPending,
    isSuccess: activeQuery.isSuccess,
    isFetching: activeQuery.isFetching,
    isError: activeQuery.isError,
  };

  const error = useMemo<Error | null>(() => (activeQuery.error ? (activeQuery.error as Error) : null), [activeQuery.error]);
  const errorMessage = useErrorMessage(error, fsErrorMessages);

  return {
    ...queryState,
    directoryData: useSharedEndpoint ? undefined : directoryQuery.data,
    fsItems,
    fsMetadata,
    error,
    errorMessage,
  };
}
